<script lang="ts" setup>
import { FwbAlert } from 'flowbite-vue';
import CommonModal from '@base-shared/components/CommonModal.vue';
import IconSvg from '@base-shared/components/IconSvg.vue';
import type { Notification } from '@stores/notification';
import type { ModalSize } from '@/types/modal';

defineProps<{
  title: string;
  subTitle?: string;
  alert?: Notification;
  persistent?: boolean;
  size?: ModalSize;
  containerClass?: string;
}>();
defineEmits<{ (e: 'close'): void }>();
</script>

<template>
  <CommonModal
    :container-class="containerClass"
    :size="size ?? 'xl'"
    :persistent="persistent"
    @close="$emit('close')"
  >
    <template #header-container>
      <div class="pt-2 px-2 pb-2 flex items-start">
        <slot name="title">
          <div
            class="pt-2.5 px-2.5 text-lg font-semibold"
          >
            {{ title }}
          </div>
        </slot>
        <button
          v-if="!persistent"
          aria-label="close"
          class="ms-auto inline-flex rounded-lg bg-transparent p-1.5 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white border-0"
          type="button"
          @click="$emit('close')"
        >
          <IconSvg
            name="flowbite:close-outline"
            class="w-5! h-5!"
          />
        </button>
      </div>
    </template>
    <template #body>
      <div class="px-5 pb-5 text-gray-500">
        <div
          v-if="subTitle"
          v-html="subTitle"
        />
        <slot />

        <div v-if="alert">
          <FwbAlert
            class="mt-4 flex"
            :class="{
              'bg-primary-50': alert.type === 'info',
              'bg-red-50': alert.type === 'danger',
              'bg-green-100': alert.type === 'success',
              '!bg-orange-50 !text-orange-800': alert.type === 'warning',
              'bg-gray-50': alert.type === 'dark',
              'flex-col items-start gap-[6px]': !!alert.title,
              'border-primary-200! border-y rounded-none': alert.showBorder && alert.type === 'info',
              'border-red-200! border-y rounded-none': alert.showBorder && alert.type === 'danger',
              'border-green-200! border-y rounded-none': alert.showBorder && alert.type === 'success',
              'border-orange-200! border-y rounded-none': alert.showBorder && alert.type === 'warning',
              'border-gray-200! border-y rounded-none': alert.showBorder && alert.type === 'dark',
            }"
            icon
            :type="alert.type"
          >
            <template #icon>
              <IconSvg
                :name="alert.icon ?? ''"
                class="w-5! h-6!"
              />
            </template>
            <template
              v-if="alert.title"
              #title
            >
              <h3 class="px-2 text-base font-semibold">
                {{ alert.title }}
              </h3>
            </template>
            <template #default>
              <div
                v-if="$slots.alertMessage"
                class="flex flex-col"
              >
                <slot name="alertMessage" />
              </div>
              <div
                v-else
                class="flex flex-col text-left"
              >
                <div class="whitespace-pre-line">
                  {{ alert.message }}
                </div>
              </div>
            </template>
          </FwbAlert>
        </div>

        <slot name="content" />

        <div
          v-if="$slots.actionButtons"
          class="mt-4 flex gap-5"
        >
          <slot name="actionButtons" />
        </div>
      </div>
    </template>
  </CommonModal>
</template>
